





























































































import {
	computed,
	defineComponent,
	PropType,
	ref,
} from '@vue/composition-api';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import SuccessCard from '@front-app/components/cards/state/SuccessCard.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size, Spacing } from '@front-app/components/constants';
import TrForm from '@front-app/components/form/Form.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import CategoriesInput from '@front-app/components/inputs/CategoriesInput.vue';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { EventBus, EventBusEvent } from '@front-app/services/EventBus';
import { ContentType, FileContentItem, LinkContentItem } from '@front-app/services/GroupsApi/DiscussionModel';
import { UpdateLinkRequest, editLink } from '@front-app/services/GroupsApi/GroupLinkApi';
import { UpdateFileMetadataRequest, editFileMetadata } from '@front-app/services/GroupsApi/GroupFileApi';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { canManageDocumentCategories } from '@front-app/util/permission';
import { syncedRef } from '@front-app/util/reactivity';

export default defineComponent({
	components: {
		CategoriesInput,
		CtaButton,
		ErrorCard,
		FormError,
		FormInput,
		FormLabel,
		FormSection,
		SuccessCard,
		TrForm,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		categories: {
			type: Array as PropType<string[]>,
			required: true,
		},
	},
	setup(props) {
		const group = computed(() => GroupsStore.getInstance().groupById(props.groupId));

		const groupContentStore = useGroupContentStore();
		const selectedContent = syncedRef(() => groupContentStore.getById(groupContentStore.selectedContentId)).ref;

		const contentName = ref(selectedContent.value?.name);
		const contentDescription = ref(selectedContent.value?.description);

		// Categories
		const selectedCategories = syncedRef(() => selectedContent.value?.categories || []).ref;
		const selectedNewCategories = ref([]);
		const canAddNewCategories = computed(() => canManageDocumentCategories(group.value));
		const removeCategory = (categoryToRemove: string) => {
			selectedCategories.value = selectedCategories.value.filter((category: string) => category !== categoryToRemove);
		};
		const removeNewCategory = (categoryToRemove: string) => {
			selectedNewCategories.value = selectedNewCategories.value.filter((category) => category !== categoryToRemove);
		};
		// END Categories

		// Validation
		const validation = v({
			name: {
				value: contentName,
				rules: [required()],
			},
		});

		const { focusFirstInvalid } = useFocusFirstInvalidField({
			containerSelector: '#edit-content-form',
			scrollConfig: { block: 'center' },
		});
		// END Validation

		// Submission
		const {
			track,
			isLoading,
			isError,
			isSuccess,
		} = useAsyncStatus();

		const editContent = async () => {
			validation.validate();
			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}

			const payloadCategories = ([] as string[])
				.concat(selectedCategories.value)
				.concat(selectedNewCategories.value);

			let updatedContent = {} as FileContentItem | LinkContentItem;

			if (selectedContent.value?.type === ContentType.FILE) {
				const payload: UpdateFileMetadataRequest = {
					name: contentName.value || '',
					description: contentDescription.value || '',
					categories: payloadCategories,
				};

				updatedContent = await track(editFileMetadata(props.groupId, selectedContent.value.id, payload));
			} else {
				const payload: UpdateLinkRequest = {
					name: contentName.value || '',
					description: contentDescription.value || '',
					categories: payloadCategories,
				};

				updatedContent = await track(editLink(props.groupId, selectedContent.value?.id || '', payload));
			}

			if (isSuccess.value) {
				groupContentStore.update([updatedContent]);

				if (selectedContent.value?.type === ContentType.FILE) {
					EventBus.emit(EventBusEvent.FILES_UPLOADED);
				} else {
					EventBus.emit(EventBusEvent.LINK_SHARED);
				}
			}
		};
		// END Submission

		return {
			ContentType,
			selectedContent,
			contentName,
			contentDescription,
			// Categories
			selectedCategories,
			selectedNewCategories,
			canAddNewCategories,
			removeCategory,
			removeNewCategory,
			// Validation
			validation,
			// Submission
			editContent,
			isError,
			isLoading,
			isSuccess,
			// Util
			ButtonPalettes,
			Size,
			Spacing,
		};
	},
});
